import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Observable, Subscription, filter } from 'rxjs';
import { Command, ConnectedAutomationsCommand } from 'src/app/model/command';
import { DeviceStatus, isAutomFwUpdateInProgress, isDeviceUnavailable } from 'src/app/model/device-status';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { selectAutomationConnectedNameEdit, selectConnectedAutomationsCommands, selectCurrentDevice, selectCurrentDeviceStatus, selectGateStates } from 'src/app/store/device/device.selector';
import { ConnectedAutomations, Device } from 'src/app/model/device';
import { GateState } from 'src/app/model/gate-state';
import { CommandAliasAutomationDialogComponent } from '../command-alias-automation-dialog/command-alias-automation-dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-support-automation',
	templateUrl: './support-automation.component.html',
	providers: [DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})

export class SupportAutomationComponent implements OnInit, OnDestroy {
	gateState$: Observable<GateState[] | undefined>;
	gateStateSub: Subscription | undefined;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	connectedAutomationsCommands$: Observable<ConnectedAutomationsCommand[] | undefined>;
	connectedAutomationsCommandsSub: Subscription | undefined;
	AutomationConnectedNameEdit$: Observable<boolean | undefined>;
	AutomationConnectedNameEditSub: Subscription | undefined;



	currentDevice: Device;
	connectedAutomation: ConnectedAutomations[];
	automationState: number[];
	gateStates: GateState[];

	commands: ConnectedAutomationsCommand[];
	deviceStatus: DeviceStatus;
	deviceOnline: boolean;
	deviceConfigured: boolean;
	ref: DynamicDialogRef | undefined;

	automationsStatus: number[];
	scanConnectedAutomation: boolean;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MAX_MD_WIDTH = 1120;
	MAX_MD_WIDTH_SHORT = 858;
	LG_WIDTH = 1500;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}


	initSelectors(): void {
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.connectedAutomationsCommands$ = this.store.select(selectConnectedAutomationsCommands);
		this.gateState$ = this.store.select(selectGateStates);
		this.device$ = this.store.select(selectCurrentDevice);
		this.AutomationConnectedNameEdit$ = this.store.select(selectAutomationConnectedNameEdit);
	}

	ngOnInit(): void {
		this.gateStateSub = this.gateState$.subscribe(
			gateStates => {
				this.gateStates = gateStates;
			}
		);
		this.deviceSub = this.device$.subscribe(currentDevice => {
			this.currentDevice = currentDevice;
			if (currentDevice !== undefined) {
				this.connectedAutomation = currentDevice.ConnectedAutomations;
				this.connectedAutomation = this.sortItems(this.connectedAutomation);
			} else {
				this.connectedAutomation = [];
			}
		});
		this.connectedAutomationsCommandsSub = this.connectedAutomationsCommands$.subscribe(
			commands => {
				this.commands = commands;
			}
		);
		this.deviceStatusSub = this.deviceStatus$.pipe(filter(ds => !!ds)).subscribe(deviceStatus => {
			if (deviceStatus !== undefined) {
				this.deviceStatus = deviceStatus;
				this.deviceOnline = deviceStatus.Online;
				this.deviceConfigured = deviceStatus.Configured === 0;
				this.automationsStatus = deviceStatus.AutomationsStatus;
				this.scanConnectedAutomation = deviceStatus.ScanConnectedAutomation;
			}
		});
		this.AutomationConnectedNameEditSub = this.AutomationConnectedNameEdit$.subscribe(edit => {
			if (edit) {
				this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId: this.currentDevice.Id }));
			}
		});
		this.onResize();
	}

	ngOnDestroy(): void {
		this.gateStateSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.connectedAutomationsCommandsSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.AutomationConnectedNameEditSub?.unsubscribe();
	}

	sortItems(connectedAutomations: ConnectedAutomations[]): ConnectedAutomations[] { // asc by Name, case-insensitive sort -> autom a and autom A is the same
		connectedAutomations.sort((a, b) =>
			a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
		);
		return connectedAutomations;
	}

	deviceConfiguredOnline(): boolean {
		return this.deviceConfigured && !isDeviceUnavailable(this.deviceStatus);
	}

	commandsUnavailableKey(): string {
		return isAutomFwUpdateInProgress(this.deviceStatus)
			? 'DEVICE_FW_UPDATE_IN_PROGRESS_ALERT'
			: 'COMMANDS_NOT_AVAILABLE';
	}

	executeCommand(commandId: number): void {
		this.store.dispatch(DeviceActions.executeCommand({ commandId }));
	}

	editAlias(automation: ConnectedAutomations): void {
		this.store.dispatch(DeviceActions.resetEditCommands());
		this.ref = this.dialogService.open(CommandAliasAutomationDialogComponent, {
			header: this.translate.instant('COMMANDS_ALIAS') + ': ' + automation.Name,
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				connectedAutomations: automation,
				commands: this.getCommands(automation)
			},
		});
	}

	automationOnline(automation: ConnectedAutomations): boolean {
		return this.getAutomationStatus(automation.CrpItemId) !== 255; // 255 STATUS OFFLINE
	}

	automationStatus(automation: ConnectedAutomations): string {
		if (this.gateStates !== undefined && this.gateStates.length > 0) {
			const st = this.getAutomationStatus(automation.CrpItemId);
			if (st != null) {
				const state = this.gateStates.find(ec => ec.Id === st);
				return state !== undefined ? state.Description : '';
			}
		}
		return '';
	}

	getAutomationStatus(crpItem: number): number {
		if (this.automationsStatus != null && this.automationsStatus.length > 1) {
			switch (crpItem) {
				case 1:
					return this.automationsStatus.length > 1 ? this.automationsStatus[1] : null;
				case 2:
					return this.automationsStatus.length > 1 ? this.automationsStatus[3] : null;
				case 3:
					return this.automationsStatus.length > 1 ? this.automationsStatus[5] : null;
				case 4:
					return this.automationsStatus.length > 1 ? this.automationsStatus[7] : null;
				default:
					return null;
			}
		}
		return null;
	}

	getCommands(automation: ConnectedAutomations): Command[] {
		if (this.commands === undefined) {
			return [];
		}
		const list = this.commands.find(cm => cm.CrpItemId === automation.CrpItemId);
		if (list !== undefined && list.Commands !== undefined) {
			return list.Commands;
		}
		return [];
	}

	manualScan(): void {
		this.store.dispatch(DeviceActions.getAutomationDiscover({ deviceId: this.currentDevice.Id }));
	}


	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	onTablet(): boolean {
		return (this.screenWidth >= this.MIN_MD_WIDTH) && (this.screenWidth <= this.MAX_MD_WIDTH_SHORT);
	}

	minWidthDialog(): string {
		return (this.screenWidth > this.MAX_MD_WIDTH) ? "w-72rem" : "";
	}

	col12Action(): string {
		return (this.screenWidth <= this.MIN_MD_WIDTH) ? "col-12" : "";
	}
}
